import { collaborationSummary, extraScheduleSummary } from "./panel-presentation.ts";
import { reminderValidationMessage } from "./draft-validation.ts";
import { resolveWallTime, validTaskTimeZone } from "./task-time.ts";
import { refreshSearchableSelections } from "../../ui/searchable-selection.ts";
import { updateScheduleSummaries } from "./task-schedule-view.ts";
import { customFieldValue } from "../custom-fields/model.ts";
import type { CustomFieldDefinition } from "../../domain/entities.ts";

export function taskFormSignature(form: HTMLFormElement): string {
  const fields = Array.from(form.elements).flatMap(element => {
    if (!(element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement || element instanceof HTMLSelectElement) || !element.name) return [];
    if (element instanceof HTMLInputElement && (element.type === "checkbox" || element.type === "radio") && !element.checked) return [`${element.name}:`];
    if (element instanceof HTMLSelectElement && element.multiple) return [`${element.name}:${Array.from(element.selectedOptions).map(option => option.value).join(",")}`];
    return [`${element.name}:${element.value}`];
  });
  const order = Array.from(form.querySelectorAll<HTMLElement>("[data-subtask]")).map(item => item.dataset.subtask).join(",");
  return `${fields.join("|")}|order:${order}`;
}

export function validateTaskTitle(form: HTMLFormElement): boolean {
  const title = form.elements.namedItem("title") as HTMLInputElement;
  const error = form.querySelector<HTMLElement>("[data-title-error]");
  const valid = !!title.value.trim();
  title.setCustomValidity(valid ? "" : "Add a title before saving.");
  if (error) error.hidden = valid;
  return valid;
}

export function updateComposerDateError(form: HTMLFormElement): boolean {
  const start = form.elements.namedItem("startDate") as HTMLInputElement | null;
  const due = form.elements.namedItem("dueDate") as HTMLInputElement | null;
  if (!start || !due) return true;
  const valid = !start.value || !due.value || start.value <= due.value;
  start.setCustomValidity(valid ? "" : "Start date must not be after the due date.");
  if (valid) start.removeAttribute("aria-invalid"); else start.setAttribute("aria-invalid", "true");
  const error = form.querySelector<HTMLElement>("[data-dates-error]");
  if (error) error.hidden = valid;
  return valid;
}

export function validateTaskForm(form: HTMLFormElement): boolean {
  if (!validateTaskTitle(form)) return false;
  for (const input of form.querySelectorAll<HTMLInputElement | HTMLSelectElement>("[data-custom-field]")) {
    input.setCustomValidity("");
    try { customFieldValue(JSON.parse(input.dataset.customField!) as CustomFieldDefinition, input.value); }
    catch (error) { input.setCustomValidity(error instanceof Error ? error.message : "Invalid field value."); }
    if (!input.checkValidity()) {
      for (let parent = input.parentElement; parent && parent !== form; parent = parent.parentElement) if (parent.tagName === "DETAILS") (parent as HTMLDetailsElement).open = true;
      input.reportValidity(); input.addEventListener("input", () => input.setCustomValidity(""), { once: true }); return false;
    }
  }
  const dueInput = form.elements.namedItem("dueDate") as HTMLInputElement;
  const due = dueInput.value;
  const reminders = Array.from(form.querySelectorAll<HTMLInputElement>("[name=exactReminderAt]"));
  const startDate = form.elements.namedItem("startDate") as HTMLInputElement;
  const startAt = form.elements.namedItem("startAt") as HTMLInputElement;
  const endAt = form.elements.namedItem("endAt") as HTMLInputElement;
  const scheduleMode = (form.elements.namedItem("scheduleMode") as HTMLSelectElement | null)?.value;
  const zoneInput = form.elements.namedItem("scheduleTimeZone") as HTMLInputElement | null;
  reminders.forEach(reminder => reminder.setCustomValidity(""));
  dueInput.setCustomValidity("");
  updateComposerDateError(form);
  endAt.setCustomValidity(startAt.value && endAt.value && endAt.value <= startAt.value ? "End time must be after the start time." : "");
  if (zoneInput) {
    const zone = zoneInput.value.trim();
    zoneInput.setCustomValidity(scheduleMode === "floating" && !validTaskTimeZone(zone) ? "Use a valid IANA timezone, such as Europe/Malta." : "");
    if (scheduleMode === "floating" && !zoneInput.validationMessage && startAt.value && endAt.value) {
      try { if (resolveWallTime(endAt.value, zone) <= resolveWallTime(startAt.value, zone)) endAt.setCustomValidity("End time must be after the start time."); }
      catch { endAt.setCustomValidity("These times cannot be resolved in the task timezone."); }
    }
  }
  reminders.forEach(reminder => reminder.setCustomValidity(reminderValidationMessage(due, reminder.value)));
  const relativeSelected = new FormData(form).getAll("reminderOffsets").length > 0;
  if (relativeSelected && !due && !startAt.value) (reminders[0] || dueInput).setCustomValidity("Relative reminders require a start time or due date.");
  const interval = form.elements.namedItem("recurrenceInterval") as HTMLInputElement;
  for (const input of [interval, startDate, endAt, dueInput, ...(zoneInput && scheduleMode === "floating" ? [zoneInput] : []), ...reminders]) {
    if (!input.checkValidity()) { if (input.closest('[data-composer-group="schedule"]')) revealComposerGroup(form, "schedule"); for (let parent = input.parentElement; parent && parent !== form; parent = parent.parentElement) if (parent.tagName === "DETAILS") (parent as HTMLDetailsElement).open = true; input.reportValidity(); return false; }
  }
  return true;
}

export function revealComposerGroup(form: HTMLFormElement, id: string): void {
  const group = form.querySelector<HTMLDetailsElement>(`[data-composer-group="${id}"]`);
  if (group) group.open = true;
}

export function updateComposerSummaries(form: HTMLFormElement, formatDate: (value: string) => string): void {
  refreshSearchableSelections(form);
  const repeatSummary = updateScheduleSummaries(form, formatDate);
  const value = (name: string) => (form.elements.namedItem(name) as HTMLInputElement | HTMLSelectElement | null)?.value || "";
  const selectedCount = (name: string) => {
    const select = form.elements.namedItem(name) as HTMLSelectElement | null;
    return select ? Array.from(select.selectedOptions).length : 0;
  };
  const label = (name: string) => {
    const select = form.elements.namedItem(name) as HTMLSelectElement | null;
    return select?.value ? select.selectedOptions[0]?.textContent || "" : "";
  };
  const calendars = form.querySelectorAll<HTMLInputElement>("[name=externalCalendars]:checked").length;
  const assignees = selectedCount("assignedTo");
  const blockers = selectedCount("dependencyIds");
  const exactReminderCount = Array.from(form.querySelectorAll<HTMLInputElement>("[name=exactReminderAt]")).filter(input => input.value && !Number.isNaN(new Date(input.value).getTime())).length;
  const reminderCount = form.querySelectorAll<HTMLInputElement>("[name=reminderOffsets]:checked").length + exactReminderCount;
  const reminderSummary = reminderCount ? `${reminderCount} reminder${reminderCount === 1 ? "" : "s"}` : "";
  const summaries: Record<string, string> = {
    custom: (() => { const count = Array.from(form.querySelectorAll<HTMLInputElement | HTMLSelectElement>("[data-custom-field]")).filter(input => input.value !== "").length; return count ? `${count} set` : "Optional"; })(),
    schedule: [form.querySelector('[data-composer-group="schedule"] [name="startDate"]') && value("startDate") ? `Starts ${formatDate(value("startDate"))}` : "", extraScheduleSummary(reminderSummary, repeatSummary, calendars, !!(value("startAt") || value("endAt")), !!value("estimatedMinutes")), (form.elements.namedItem("checkpoint") as HTMLInputElement | null)?.checked ? "Checkpoint" : ""].filter(Boolean).join(" · "),
    organization: label("cycleId"),
    collaboration: [collaborationSummary(label("assignedTo"), assignees, blockers), value("parentTaskId") ? "Child task" : "", value("duplicateTaskId") || selectedCount("relatedTaskIds") ? "Relationships" : ""].filter(Boolean).join(" · "),
    status: (form.elements.namedItem("completed") as HTMLInputElement | null)?.checked ? "Completed" : "",
  };
  Object.entries(summaries).forEach(([id, summary]) => {
    const target = form.querySelector<HTMLElement>(`[data-composer-group="${id}"] > summary small`);
    if (target) { target.textContent = summary; target.hidden = !summary; }
  });
}
